/**
 * 外部认证跳转服务。
 *
 * 职责：
 * 1. 判断外部登录入口是否已配置；
 * 2. 生成携带 redirect 参数的登录地址；
 * 3. 为路由守卫和会话失效提示提供统一、可测试的登录跳转结果。
 */

import { apiConfig } from '@/services/api/config';

/**
 * 外部认证跳转配置。
 */
export interface ExternalAuthRedirectConfig {
  /**
   * 外部登录地址。
   */
  externalAuthUrl: string;

  /**
   * 登录地址是否由环境变量显式配置。
   */
  externalAuthUrlConfigured: boolean;
}

/**
 * 登录跳转地址解析结果。
 */
export type LoginRedirectUrlResolution =
  | {
      /**
       * 已成功生成跳转地址。
       */
      status: 'ready';

      /**
       * 携带 redirect 参数的完整登录地址。
       */
      url: string;
    }
  | {
      /**
       * 登录地址未配置。
       */
      status: 'missing-config';

      /**
       * 给用户或错误页展示的说明。
       */
      message: string;
    }
  | {
      /**
       * 登录地址格式无效。
       */
      status: 'invalid-config';

      /**
       * 给用户或错误页展示的说明。
       */
      message: string;
    };

/**
 * 外部认证跳转服务。
 */
export class ExternalAuthRedirectService {
  /**
   * 外部认证跳转配置。
   */
  private readonly config: ExternalAuthRedirectConfig;

  /**
   * 创建外部认证跳转服务。
   *
   * @param config 外部认证跳转配置。
   */
  public constructor(config: ExternalAuthRedirectConfig) {
    this.config = config;
  }

  /**
   * 生成外部登录地址。
   *
   * @param returnUrl 登录成功后应回跳的当前页面地址。
   * @returns 登录跳转地址解析结果。
   */
  public resolveLoginRedirectUrl(returnUrl: string): LoginRedirectUrlResolution {
    const externalAuthUrl = this.config.externalAuthUrl.trim();

    if (!this.config.externalAuthUrlConfigured || externalAuthUrl.length === 0) {
      return {
        status: 'missing-config',
        message: '登录已失效，请配置 VITE_EXTERNAL_AUTH_URL 后重新登录。',
      };
    }

    try {
      const url = new URL(externalAuthUrl, this.resolveBaseUrl());
      /**
       * redirect 使用 searchParams 统一写入，避免外部登录地址本身已有 query 时拼接错误。
       */
      url.searchParams.set('redirect', returnUrl);
      return {
        status: 'ready',
        url: url.toString(),
      };
    } catch {
      return {
        status: 'invalid-config',
        message: '登录地址格式无效，请检查 VITE_EXTERNAL_AUTH_URL。',
      };
    }
  }

  /**
   * 解析 URL 构造器所需的基准地址。
   *
   * @returns 当前页面源地址；非浏览器环境下返回测试用默认源。
   */
  private resolveBaseUrl(): string {
    if (typeof window === 'undefined') {
      return 'http://localhost';
    }

    return window.location.origin;
  }
}

/**
 * 默认外部认证跳转服务实例。
 */
export const externalAuthRedirectService = new ExternalAuthRedirectService({
  externalAuthUrl: apiConfig.externalAuthUrl,
  externalAuthUrlConfigured: apiConfig.externalAuthUrlConfigured,
});
